import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Trash2, Sparkles } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface DetectedFood {
  name: string;
  portion_grams: number;
  confidence: number;
  notes?: string;
  // AI-estimated nutrition
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

interface DetectedFoodReviewModalProps {
  open: boolean;
  onClose: () => void;
  detectedFoods: DetectedFood[];
  onConfirm: (foods: DetectedFood[]) => void;
  mealType: string;
}

export const DetectedFoodReviewModal = ({ 
  open, 
  onClose, 
  detectedFoods,
  onConfirm,
  mealType 
}: DetectedFoodReviewModalProps) => {
  const { t } = useTranslation();
  const [foods, setFoods] = useState<DetectedFood[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (detectedFoods.length > 0) {
      // Foods already have AI-estimated nutrition from the edge function
      setFoods([...detectedFoods]);
    }
  }, [detectedFoods]);

  const updateFood = (index: number, updates: Partial<DetectedFood>) => {
    const updated = [...foods];
    const oldFood = updated[index];
    updated[index] = { ...oldFood, ...updates };
    
    // Recalculate nutrition proportionally if portion changed
    if (updates.portion_grams && oldFood.portion_grams) {
      const ratio = updates.portion_grams / oldFood.portion_grams;
      
      updated[index] = {
        ...updated[index],
        calories: Math.round(oldFood.calories * ratio),
        protein: Math.round(oldFood.protein * ratio * 10) / 10,
        carbs: Math.round(oldFood.carbs * ratio * 10) / 10,
        fat: Math.round(oldFood.fat * ratio * 10) / 10,
        fiber: Math.round(oldFood.fiber * ratio * 10) / 10,
      };
    }
    
    setFoods(updated);
  };

  const removeFood = (index: number) => {
    setFoods(foods.filter((_, i) => i !== index));
  };

  const handleConfirm = async () => {
    if (foods.length === 0) {
      toast({
        title: t('components.detectedReview.noFoodsTitle'),
        description: t('components.detectedReview.emptyError'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(foods);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalNutrition = foods.reduce((acc, food) => ({
    calories: acc.calories + (food.calories || 0),
    protein: acc.protein + (food.protein || 0),
    carbs: acc.carbs + (food.carbs || 0),
    fat: acc.fat + (food.fat || 0),
    fiber: acc.fiber + (food.fiber || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md flex flex-col max-h-[75vh] p-0">
        <DialogHeader className="px-4 pt-4 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            {t('components.detectedReview.titlePrefix')}{mealType}
          </DialogTitle>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto px-4 min-h-0 ios-modal-scroll"
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
          }}
        >
          <div className="space-y-3 pb-3">
            {foods.map((food, index) => (
              <div key={index} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Input
                        value={food.name}
                        onChange={(e) => updateFood(index, { name: e.target.value })}
                        className="font-medium h-8 text-sm"
                      />
                      <Badge variant={food.confidence >= 70 ? "default" : "secondary"} className="text-xs">
                        {food.confidence}%
                      </Badge>
                    </div>
                    
                    {food.notes && (
                      <p className="text-xs text-muted-foreground">{food.notes}</p>
                    )}
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => removeFood(index)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">{t('components.detectedReview.portion')}</Label>
                    <Input
                      type="number"
                      value={food.portion_grams}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || food.portion_grams;
                        updateFood(index, { portion_grams: Math.max(10, Math.min(500, value)) });
                      }}
                      className="w-20 h-7 text-center text-sm"
                      min={10}
                      max={500}
                    />
                  </div>
                  <div
                    className="px-1 py-2 ios-slider-wrapper"
                    style={{
                      touchAction: 'none',
                      WebkitTouchCallout: 'none',
                      WebkitTapHighlightColor: 'transparent',
                      WebkitUserSelect: 'none',
                      pointerEvents: 'auto',
                    }}
                  >
                    <Slider
                      key={`slider-${index}`}
                      value={[food.portion_grams]}
                      onValueChange={([value]) => {
                        updateFood(index, { portion_grams: Math.round(value) });
                      }}
                      min={10}
                      max={500}
                      step={5}
                      className="w-full cursor-pointer ios-slider-fix"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-5 gap-1 text-xs">
                  <div className="text-center p-1.5 bg-muted rounded">
                    <div className="font-semibold">{food.calories}</div>
                    <div className="text-muted-foreground text-[10px]">{t('components.detectedReview.macroCal')}</div>
                  </div>
                  <div className="text-center p-1.5 bg-muted rounded">
                    <div className="font-semibold">{food.protein}g</div>
                    <div className="text-muted-foreground text-[10px]">{t('components.detectedReview.macroProtein')}</div>
                  </div>
                  <div className="text-center p-1.5 bg-muted rounded">
                    <div className="font-semibold">{food.carbs}g</div>
                    <div className="text-muted-foreground text-[10px]">{t('components.detectedReview.macroCarbs')}</div>
                  </div>
                  <div className="text-center p-1.5 bg-muted rounded">
                    <div className="font-semibold">{food.fat}g</div>
                    <div className="text-muted-foreground text-[10px]">{t('components.detectedReview.macroFat')}</div>
                  </div>
                  <div className="text-center p-1.5 bg-muted rounded">
                    <div className="font-semibold">{food.fiber}g</div>
                    <div className="text-muted-foreground text-[10px]">{t('components.detectedReview.macroFiber')}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t px-4 py-3 shrink-0 bg-background">
          <h4 className="font-semibold mb-1.5 text-sm">{t('components.detectedReview.total')}</h4>
          <div className="grid grid-cols-5 gap-1 text-xs mb-3">
            <div className="text-center p-1.5 bg-primary/10 rounded">
              <div className="font-bold">{Math.round(totalNutrition.calories)}</div>
              <div className="text-muted-foreground text-[10px]">{t('components.detectedReview.macroCal')}</div>
            </div>
            <div className="text-center p-1.5 bg-primary/10 rounded">
              <div className="font-bold">{totalNutrition.protein.toFixed(1)}g</div>
              <div className="text-muted-foreground text-[10px]">{t('components.detectedReview.macroProtein')}</div>
            </div>
            <div className="text-center p-1.5 bg-primary/10 rounded">
              <div className="font-bold">{totalNutrition.carbs.toFixed(1)}g</div>
              <div className="text-muted-foreground text-[10px]">{t('components.detectedReview.macroCarbs')}</div>
            </div>
            <div className="text-center p-1.5 bg-primary/10 rounded">
              <div className="font-bold">{totalNutrition.fat.toFixed(1)}g</div>
              <div className="text-muted-foreground text-[10px]">{t('components.detectedReview.macroFat')}</div>
            </div>
            <div className="text-center p-1.5 bg-primary/10 rounded">
              <div className="font-bold">{totalNutrition.fiber.toFixed(1)}g</div>
              <div className="text-muted-foreground text-[10px]">{t('components.detectedReview.macroFiber')}</div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="flex-1 h-9">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleConfirm} disabled={isSubmitting || foods.length === 0} className="flex-1 h-9">
              {isSubmitting
                ? t('components.detectedReview.adding')
                : foods.length === 1
                  ? t('components.detectedReview.addOne')
                  : t('components.detectedReview.addMany', { count: foods.length })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
